import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TenantCtx = createContext({
    slug: "tadasuni",
    tenant: null,
    loading: true,
    reload: () => {},
});

// Parse the "?borgo=slug" query from the HashRouter hash portion
// (URL structure like https://.../#/eventi?borgo=orgosolo)
function readSlugFromLocation() {
    try {
        const hash = window.location.hash || "";
        const qIndex = hash.indexOf("?");
        if (qIndex >= 0) {
            const qs = new URLSearchParams(hash.substring(qIndex + 1));
            const b = qs.get("borgo");
            if (b) return b.toLowerCase();
        }
        // Also check regular query params
        const params = new URLSearchParams(window.location.search);
        const b2 = params.get("borgo");
        if (b2) return b2.toLowerCase();
        // Fallback to the logged-in admin's tenant_slug (persisted at login)
        const stored = localStorage.getItem("tada_tenant_slug");
        if (stored) return stored.toLowerCase();
    } catch (_e) {
        // ignore
    }
    return "tadasuni";
}

export const TenantProvider = ({ children }) => {
    const [slug, setSlug] = useState(readSlugFromLocation());
    const [tenant, setTenant] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/tenants/public/${slug}`);
            setTenant(data);
        } catch {
            setTenant(null);
        } finally {
            setLoading(false);
        }
    }, [slug]);

    useEffect(() => { load(); }, [load]);

    // Update slug when hash changes or when the logged-in tenant changes
    useEffect(() => {
        const refresh = () => setSlug(readSlugFromLocation());
        window.addEventListener("hashchange", refresh);
        window.addEventListener("tenantslugchange", refresh);
        return () => {
            window.removeEventListener("hashchange", refresh);
            window.removeEventListener("tenantslugchange", refresh);
        };
    }, []);

    return (
        <TenantCtx.Provider value={{ slug, tenant, loading, reload: load, setSlug }}>
            {children}
        </TenantCtx.Provider>
    );
};

export const useTenant = () => useContext(TenantCtx);

// Public API axios helper (auto-appends ?borgo=slug)
export const publicApi = (slug) => axios.create({
    baseURL: API,
    params: { borgo: slug || "tadasuni" },
});
